import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { IdType } from '../ReactreeUtils.js';
import Translate from '../../../core/Translate';

const KEYCODE = {
	ESC: 27,
	ENTER: 13
};

const PAGING_SIZE = 50;

class ReactreeNode extends Component {
	constructor(props, context) {
		super(props);

		this.subscribeListener = this.subscribeListener.bind(this);
		this.handleDropdownClick = this.handleDropdownClick.bind(this);
		this.handleNodeClick = this.handleNodeClick.bind(this);
		this.handleExpandToggle = this.handleExpandToggle.bind(this);
		this.handleAddClick = this.handleAddClick.bind(this);
		this.handleRenameClick = this.handleRenameClick.bind(this);
		this.renameNode = this.renameNode.bind(this);
		this.handleRemoveClick = this.handleRemoveClick.bind(this);
		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleInputBlur = this.handleInputBlur.bind(this);
		this.setEditMode = this.setEditMode.bind(this);
		this.cancelEdit = this.cancelEdit.bind(this);
		this.handleInputKeyDown = this.handleInputKeyDown.bind(this);

		this.handleDragStart = this.handleDragStart.bind(this);
		this.handleDragOver = this.handleDragOver.bind(this);
		this.handleDragEnd = this.handleDragEnd.bind(this);
		this.handleDragEnter = this.handleDragEnter.bind(this);
		this.handleDragLeave = this.handleDragLeave.bind(this);
		this.handleDrop = this.handleDrop.bind(this);
		this.handleMoreButtonClick = this.handleMoreButtonClick.bind(this);
		

		this.getChildrenElements = this.getChildrenElements.bind(this);
		this.getCurrentMaxSize = this.getCurrentMaxSize.bind(this);
		this.getNodeNameEditorElement = this.getNodeNameEditorElement.bind(this);

		const node = context.getNode(this.props.id);

		this.state = {
			isInitialized: this.props.isRoot || context.isExpandRootOnInit(),
			// isInitialized: context.isExpandRootOnInit(),
			isExpanded: this.props.isRoot || context.isExpandRootOnInit(),
			// isExpanded: context.isExpandRootOnInit(),
			isActive: this.props.id === context.getActiveNodeId(),
			isDroppable: false,
			node: node,
			newName: node.name,
			page: 1
		};
	}

	componentWillReceiveProps(nextProps) {
		this.setState({
			node: this.context.getNode(nextProps.id),
			isActive: nextProps.id === this.context.getActiveNodeId()
		});
	}

	shouldComponentUpdate(nextProps, nextState) {
		// return true;
		const thisNode = this.state.node;
		const nextNode = nextState.node;
		if(this.context.getNode(this.props.id) !== thisNode){
			return true;
		}
		if (this.state.isExpanded !== nextState.isExpanded) {
			return true;
		}
		if (this.state.isDroppable !== nextState.isDroppable) {
			return true;
		}
		if (this.state.newName !== nextState.newName) {
			return true;
		}
		if (this.state.page !== nextState.page) {
			return true;
		}
		if (this.state.isActive !== nextState.isActive) {
			return true;
		}
		if (thisNode.name !== nextNode.name) {
			return true;
		}
		if (thisNode.id !== nextNode.id) {
			return true;
		}
		if (thisNode.options.isEditingMode !== nextNode.options.isEditingMode) {
			return true;
		}
		if (thisNode.options.isNew !== nextNode.options.isNew) {
			return true;
		}
		if (JSON.stringify(thisNode.children) !== JSON.stringify(nextNode.children)) {
			return true;
		}
		return false;
	}

	componentDidMount() {
		this.context.subscribe(this.props.id, this.subscribeListener);
	}

	componentWillUnmount() {
		this.context.unsubscribe(this.props.id);
	}

	subscribeListener() {
		const nextState = {
			node: this.context.getNode(this.props.id),
			isActive: this.context.isActiveNodeOnClick() && (this.props.id === this.context.getActiveNodeId())
		};
		if (nextState.node.name !== this.state.node.name) {
			nextState.newName = nextState.node.name;
		}
		if (nextState.node.children.length > this.state.node.children.length) {
			if (!this.state.isInitialized) {
				nextState.isInitialized = true;
			}
			nextState.isExpanded = true;
		}
		if (nextState.isExpanded && !this.state.isExpanded) {
			nextState.page = 1;
		}
		this.setState(nextState);
	}

	stopPropagation(event) {
		event.stopPropagation();
		event.nativeEvent.stopImmediatePropagation();
	}

	handleNodeClick() {
		if (!this.context.onNodeClick(this.state.node)) {
			this.handleExpandToggle();
		}
	}

	handleExpandToggle() {
		if (this.state.node.children.length) {
			const nextState = {};
			if (!this.state.isInitialized) {
				nextState.isInitialized = true;
			}
			nextState.isExpanded = !this.state.isExpanded;
			if (nextState.isExpanded) {
				nextState.page = 1;
			}
			this.setState(nextState);
		}
	}

	handleAddClick() {
		this.context.addNode(this.props.id);
	}

	handleRenameClick() {
		this.renameNode();
	}

	renameNode(force) {
		this.context.renameNode(this.props.id, this.state.newName, !!force)
	}

	handleRemoveClick() {
		this.context.removeNode(this.props.id);
	}

	handleInputChange(event) {
		this.setState({ newName: event.target.value });
	}

	handleInputFocus(event) {
		event.target.select();
	}

	handleInputBlur() {
		if (!!this.state.newName) {
			this.renameNode(!this.state.node.name);
		} else {
			this.cancelEdit();
		}
	}

	handleInputKeyDown(event) {
		const keyCode = event.keyCode;

		if (keyCode === KEYCODE.ENTER && !!this.state.newName) {
			this.renameNode(!this.state.node.name);
		} else if (keyCode === KEYCODE.ESC) {
			this.cancelEdit();
		}
	}

	setEditMode(isEditingMode) {
		if (!isEditingMode) {
			this.setState({ newName: this.state.node.name });
		}
		
		this.context.setEditMode(this.props.id, isEditingMode);
	}

	cancelEdit() {
		if (!!this.state.node.name) {
			this.setEditMode(false);
		} else {
			this.context.removeNode(this.props.id, true);
		}
	}

	handleDropdownClick(event) {
		this.stopPropagation(event);
		this.context.toggleDropdown({
			targetElement: event.currentTarget,
			targetNodeId: this.props.id,
			isRoot: !!this.props.isRoot,
			onAddClick: this.handleAddClick,
			onRenameClick: this.setEditMode.bind(this, true),
			onRemoveClick: this.handleRemoveClick
		});
	}

	handleDragStart(event) {
		if (event && !this.props.isRoot) {
			this.stopPropagation(event);
			if (!this.context.isDisableDrag(this.props.id)) {
				event.dataTransfer.effectAllowed = 'move';
				event.dataTransfer.setData('text', JSON.stringify(this.state.node));
			} else {
				event.preventDefault();
			}
		}
	}

	handleDragEnter(event) {
		if (event) {
			this.stopPropagation(event);
			if (!this.context.isDisableDrop(this.props.id)) {
				this.setState({ isDroppable: true });
			}
		}
	}

	handleDragLeave(event) {
		if (event) {
			this.stopPropagation(event);
			this.setState({ isDroppable: false });
		}
	}

	handleDragOver(event) {
		if (event) {
			event.preventDefault();
			this.stopPropagation(event);
			if (!this.context.isDisableDrop(this.props.id)) {
				this.setState({ isDroppable: true });
			}
		}
	}

	handleDragEnd(event) {
		if (event) {
			this.stopPropagation(event);
		}
	}

	handleDrop(event) {
		if (event) {
			// 내가 드롭한 곳의 정보를 정확하게 가져오기 위해 다음 구문 실행
			this.stopPropagation(event);
			if (!this.context.isDisableDrop(this.props.id)) {
				const txt =  event.dataTransfer.getData('text');
				if(!txt || !txt.trim()){
					return;
				}
				const node = JSON.parse(txt);
				const droppedNodeId = this.props.id;
				if (node.id !== droppedNodeId && node.parent !== droppedNodeId) {
					this.context.moveNode(node.id, node.parent, droppedNodeId);
				}
				this.setState({ isDroppable: false });
			}
		}
	}

	handleMoreButtonClick() {
		if (this.getCurrentMaxSize() < this.state.node.children.length) {
			this.setState((prevState) => ({ page: prevState.page + 1 }));
		}
	}

	getChildrenElements() {
		const elements = [];
		if (this.state.isInitialized) {
			for (let index = 0; index < this.getCurrentMaxSize(); index++) {
				const child = this.state.node.children[index];
				elements.push(<ReactreeNode id={child} key={child} />);
			}
		}
		return elements;
	}

	getCurrentMaxSize() {
		return Math.min(this.state.node.children.length, this.state.page * PAGING_SIZE);
	}

	getNodeNameEditorElement() {
		if (this.state.node.options.isEditingMode) {
			return (
				<input
					type="text"
					value={this.state.newName}
					onChange={this.handleInputChange}
					onFocus={this.handleInputFocus}
					onBlur={this.handleInputBlur}
					onKeyDown={this.handleInputKeyDown}
					placeholder={Translate.get('$S_msg_PleaseEnterName')}
					autoFocus
				/>
			);
		}
		return;
	}

	render() {
		const currentNode = this.state.node;

		const loadMoreButton = this.getCurrentMaxSize() < currentNode.children.length ? (
			<li>
				<div className="node-container btn-container">
					<button className="btn-load-more" onClick={this.handleMoreButtonClick}>
						{PAGING_SIZE} {Translate.get('$S_str_MoreViews')}
					</button>
				</div>
			</li>
		) : null;

		const dropdownButton = (!this.context.isDisableDropdown(this.props.id) && !currentNode.options.isNew) ? (
			<button className="btn-dropdown-toggle" onClick={this.handleDropdownClick}>
				<i className="fa fa-angle-down"></i>
			</button>
		) : null;

		const additionalIcon = !!currentNode.options.iconClass ? <i className={currentNode.options.iconClass}></i> : null;

		return (						
			<li className={cx({ root: this.props.isRoot, droppable: this.state.isDroppable, active: this.state.isActive })} draggable={!this.props.isRoot}
				onDragStart={this.handleDragStart}
				onDragOver={this.handleDragOver}
				onDragEnd={this.handleDragEnd}
				onDragEnter={this.handleDragEnter}
				onDragLeave={this.handleDragLeave}
				onDrop={this.handleDrop}>
				<div className="node-container">
					<div className="node-name-wrap">
						<i
							className={
								cx('btn-toggle fa', {
									'invisible': !this.state.node.children.length,
									'fa-plus': !this.state.isExpanded,
									'fa-minus': this.state.isExpanded
								})
							}
							onClick={this.handleExpandToggle}
						></i>
						<span
							className={cx('node-name', { 'hide': this.state.node.options.isEditingMode })}
							
						>
							{additionalIcon}
							<span className={'unselectable'} draggable={false} onClick={this.handleNodeClick}>{this.state.node.name}</span>
						</span>
						{this.getNodeNameEditorElement()}
					</div>
					{dropdownButton}
				</div>
				<ul className={cx({ hide: !this.state.isExpanded })}>
					{this.getChildrenElements()}
					{loadMoreButton}
				</ul>
			</li>
		);
	}
}

ReactreeNode.contextTypes = {
	toggleDropdown: PropTypes.func,
	onNodeClick: PropTypes.func,
	addNode: PropTypes.func,
	renameNode: PropTypes.func,
	removeNode: PropTypes.func,
	moveNode: PropTypes.func,
	setEditMode: PropTypes.func,
	getNode: PropTypes.func,
	getActiveNodeId: PropTypes.func,
	isActiveNodeOnClick: PropTypes.func,
	isExpandRootOnInit: PropTypes.func,
	isDisableDropdown: PropTypes.func,
	isDisableDrag: PropTypes.func,
	isDisableDrop: PropTypes.func,
	subscribe: PropTypes.func,
	unsubscribe: PropTypes.func,
	notifyChange: PropTypes.func
};

ReactreeNode.propTypes = {
	id: IdType,
	isRoot: PropTypes.bool
};

ReactreeNode.defaultProps = {
	isRoot: false
};

export default ReactreeNode;


// WEBPACK FOOTER //
// ./resources/WEB-INF/src/ui/Reactree/ReactreeNode/index.jsx